import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../api';
import { useToast } from '../contexts/ToastContext';
import './MenuManagement.css';

const MenuManagement = () => {
    const { success, error: toastError } = useToast();
    const [configs, setConfigs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [saving, setSaving] = useState(false);

    const roles = ['admin', 'trainer', 'viewer', 'localadmin'];

    const allMenuItems = [
        // Основне
        { id: 'dashboard', label: 'Панель', section: 'Основне меню' },
        { id: 'students', label: 'Гравці', section: 'Основне меню' },
        { id: 'courses', label: 'Навчання', section: 'Основне меню' },
        { id: 'guide', label: 'Як користуватися', section: 'Основне меню' },
        { id: 'analytics', label: 'Аналітика', section: 'Основне меню' },

        // Суб-вкладки Гравці
        { id: 'test-results', label: 'Результати тестів', section: 'Суб-вкладки Гравці' },
        { id: 'assignments', label: 'Менеджер призначень', section: 'Суб-вкладки Гравці' },

        // Суб-вкладки Навчання
        { id: 'builders', label: 'Інтерактивність (Хаб)', section: 'Суб-вкладки Навчання' },
        { id: 'library', label: 'Бібліотека медіа', section: 'Суб-вкладки Навчання' },

        // Суб-вкладки Адміністрування
        { id: 'users', label: 'Користувачі', section: 'Суб-вкладки Адмін' },
        { id: 'cities', label: 'Міста', section: 'Суб-вкладки Адмін' },
        { id: 'departments', label: 'Підрозділи', section: 'Суб-вкладки Адмін' },
        { id: 'menu-management', label: 'Управління меню', section: 'Суб-вкладки Адмін' },
        { id: 'dishes', label: 'База посуду', section: 'Суб-вкладки Адмін' },
        { id: 'settings', label: 'Обслуговування', section: 'Суб-вкладки Admin' },

        // Інтерактивність
        { id: 'quiz-builder', label: 'Квіз (Тести)', section: 'Інтерактивність' },
        { id: 'virtual-desk', label: 'Сервірування столу', section: 'Інтерактивність' },
        { id: 'visual-builder', label: 'Візуальний редактор ігор', section: 'Інтерактивність' },
        { id: 'complex-builder', label: 'Комплексні тести', section: 'Інтерактивність' },
        { id: 'certificates', label: 'Шаблони сертифікатів', section: 'Інтерактивність' },
        { id: 'pairing-builder', label: 'Конструктор піци', section: 'Інтерактивність' }
    ];

    const fetchConfigs = useCallback(async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await axios.get(`${API_URL}/menu-config`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            console.log('Fetched configs:', res.data);
            setConfigs(Array.isArray(res.data) ? res.data : []);
            setLoading(false);
        } catch (err) {
            console.error('Fetch error:', err);
            const errorMsg = 'Не вдалося завантажити налаштування меню';
            setError(errorMsg);
            toastError(errorMsg);
            setLoading(false);
            setConfigs([]);
        }
    }, [toastError]);

    useEffect(() => {
        fetchConfigs();
    }, [fetchConfigs]);

    const handleToggle = (role, itemId) => {
        const currentConfigs = Array.isArray(configs) ? configs : [];
        const config = currentConfigs.find(c => c.role === role) || { role, hiddenItems: [] };
        let newHiddenItems;
        if (config.hiddenItems && Array.isArray(config.hiddenItems)) {
            if (config.hiddenItems.includes(itemId)) {
                newHiddenItems = config.hiddenItems.filter(id => id !== itemId);
            } else {
                newHiddenItems = [...config.hiddenItems, itemId];
            }
        } else {
            newHiddenItems = [itemId];
        }

        const newConfigs = [...currentConfigs.filter(c => c.role !== role), { ...config, hiddenItems: newHiddenItems }];
        setConfigs(newConfigs);
    };

    const handleSave = async (role) => {
        setSaving(true);
        try {
            const token = localStorage.getItem('token');
            const currentConfigs = Array.isArray(configs) ? configs : [];
            const config = currentConfigs.find(c => c.role === role) || { role, hiddenItems: [] };
            await axios.put(`${API_URL}/menu-config/${role}`, {
                hiddenItems: config.hiddenItems || []
            }, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setError('');
            success(`Налаштування для ролі ${role} збережено!`);
        } catch (err) {
            const errorMsg = `Помилка при збереженні для ролі ${role}`;
            setError(errorMsg);
            toastError(errorMsg);
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <div className="menu-mgmt-loading">Завантаження...</div>;

    // Direct inline fallback for absolute safety
    const renderConfigs = Array.isArray(configs) ? configs : [];

    return (
        <div className="menu-mgmt-container">
            <header className="menu-mgmt-header">
                <h2>Управління боковим меню</h2>
                <p>Тут ви можете приховати певні пункти меню для різних ролей. <strong>Superadmin</strong> завжди бачить усі пункти.</p>
            </header>

            {error && <div className="error-message">{error}</div>}

            <div className="menu-mgmt-grid">
                {roles.map(role => {
                    const config = renderConfigs.find(c => c && c.role === role) || { role, hiddenItems: [] };
                    return (
                        <div key={role} className="role-config-card">
                            <div className="role-config-header">
                                <h3 className={`role-title ${role}`}>{role}</h3>
                                <button 
                                    className="btn-save-mini" 
                                    onClick={() => handleSave(role)}
                                    disabled={saving}
                                >
                                    {saving ? '...' : 'Зберегти'}
                                </button>
                            </div>
                            <div className="menu-items-list">
                                {allMenuItems.map(item => (
                                    <label key={item.id} className="menu-item-checkbox">
                                        <input
                                            type="checkbox"
                                            checked={config.hiddenItems ? !config.hiddenItems.includes(item.id) : true}
                                            onChange={() => handleToggle(role, item.id)}
                                        />
                                        <span className="checkbox-custom"></span>
                                        <span className="item-label">{item.label}</span>
                                        <span className="item-section">({item.section})</span>
                                    </label>
                                ))}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default MenuManagement;
